import { z } from 'zod';
export const ConnectionStatusSchema = z.enum(['pending', 'accepted', 'declined']);
export const ConnectionSourceSchema = z.enum(['qr_scan', 'request']);
export const ConnectionSchema = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    /** The attendee who INITIATED. Since 0037 this is a fact, not a uuid sort. */
    requesterAttendeeId: z.string().uuid(),
    recipientAttendeeId: z.string().uuid(),
    status: ConnectionStatusSchema,
    source: ConnectionSourceSchema,
    createdAt: z.string(),
    decidedAt: z.string().nullable(),
});
/**
 * `eventId` on the wire.
 *
 * A UUID **or a slug** — every connections route resolves it through
 * `eventsWorkflow.get`, which accepts both, and the mobile app sends whichever
 * one `/v1/me` handed it. A `.uuid()` here would refuse payloads the server
 * accepts, which is the cross-seam drift docs/testing-discipline.md describes.
 */
export const ConnectionEventIdSchema = z.string().min(1).max(80);
export const QrScanRequestSchema = z.object({
    eventId: ConnectionEventIdSchema,
    qrToken: z.string().min(8).max(128),
});
export const RequestConnectionRequestSchema = z.object({
    eventId: ConnectionEventIdSchema,
    recipientAttendeeId: z.string().uuid(),
});
export const DecideConnectionRequestSchema = z.object({
    eventId: ConnectionEventIdSchema,
    status: z.enum(['accepted', 'declined']),
});
/**
 * Which way a connection runs, decided by the SERVER.
 *
 * The client must never compute this by comparing `requesterAttendeeId` to its
 * own id: that inference was correct only half the time under the old storage
 * scheme, and there is no reason to reintroduce the possibility.
 */
export const ConnectionDirectionSchema = z.enum(['outgoing', 'incoming']);
/**
 * What a mutation actually did, so no client has to infer it from the row.
 *
 * `already_requested` deliberately covers two different stored states — a
 * request still waiting, and one the other party declined. The server will not
 * tell the sender which, and no client should try to work it out.
 */
export const RequestOutcomeSchema = z.enum([
    'requested',
    'already_requested',
    'accepted',
    'already_connected',
]);
/**
 * What `POST /v1/connections/qr-scan` can answer.
 *
 * A scan normally connects the pair. It answers `already_requested` in one case:
 * the badge's owner has already declined the scanner. `qrToken` is a permanent
 * unsigned bearer string that a photograph of a printed badge carries, so a scan
 * is not proof of consent from the person scanned — and a refusal already on
 * record must not be erasable by holding their token. That scanner is answered
 * exactly as a repeat request answers them, over a row serialized exactly the
 * same way, so the scan does not disclose the decline either.
 *
 * A subset of {@link RequestOutcomeSchema}, which is what the mutation response
 * still parses against: one row parser, one outcome parser, both endpoints.
 */
export const QrScanOutcomeSchema = z.enum(['accepted', 'already_connected', 'already_requested']);
/** The other party's public profile, enough to render a row without a second call. */
export const ConnectionPeerSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    title: z.string().nullable(),
    company: z.string().nullable(),
    avatarPath: z.string().nullable(),
    socialLinks: z.record(z.string()),
});
/**
 * One row of `GET /v1/connections`, and of every connections mutation response.
 *
 * Strictly additive over `ConnectionSchema`: every key the shipped mobile app
 * reads is still present, in the type it reads (ADR 0015).
 *
 * `canDecide` is false for every ACCEPTED row regardless of direction — the
 * stored order on a connection made before 0037 is arbitrary, and there is
 * nothing left to decide on one anyway. `canWithdraw` is true for both parties
 * of an accepted row (disconnect), for the sender of a pending one (withdraw),
 * and for the decliner of a declined one (clearing it away).
 *
 * A DECLINED ROW IS NEVER SERVED TO THE PERSON WHO WAS DECLINED. The server
 * serializes it to them as `status: "pending"`, `decidedAt: null`,
 * `direction: "outgoing"`, `canDecide: false`, `canWithdraw: true` — the exact
 * shape one of their own requests still waiting produces, down to the byte. The
 * decliner sees the row as it really is, because the decision is theirs.
 *
 * Two consequences for any client:
 *
 *  - `status` on an OUTGOING row means "what you are allowed to be told", not
 *    "what is stored". Do not build anything that needs to distinguish the two;
 *    there is deliberately nothing on the wire that can.
 *  - "Withdraw" on an outgoing row is not always a delete. The server may keep
 *    the row and simply stop serving it — which is why the response is a bare
 *    204 and the client's cue to drop the row is that 204, not a status change.
 */
export const ConnectionListItemSchema = ConnectionSchema.extend({
    myAttendeeId: z.string().uuid(),
    direction: ConnectionDirectionSchema,
    canDecide: z.boolean(),
    canWithdraw: z.boolean(),
    other: ConnectionPeerSchema.nullable(),
});
export const ConnectionsListResponseSchema = z.object({
    data: z.array(ConnectionListItemSchema),
    meta: z.object({ nextCursor: z.string().nullable() }),
});
/**
 * `POST /v1/connections/request` and `POST /v1/connections/qr-scan`.
 *
 * The row comes back ENRICHED — the same shape the list serves — so a client
 * can draw what it just changed without re-reading the whole list first, and
 * so there is one row parser rather than two. A parser written for two shapes
 * is a lenient parser, and a lenient parser is how `photoPath` survived being
 * renamed to `avatarPath` with no avatar rendering anywhere and nothing failing.
 */
export const ConnectionMutationResponseSchema = z.object({
    data: z.object({
        connection: ConnectionListItemSchema,
        outcome: RequestOutcomeSchema,
    }),
});
/** `POST /v1/connections/:id/decide` — the enriched row, on its own. */
export const ConnectionDecideResponseSchema = z.object({
    data: ConnectionListItemSchema,
});
/**
 * The largest page `GET /v1/connections?limit=` will serve.
 *
 * There is deliberately NO default: `limit` absent means the whole list,
 * because the shipped app sends none and cannot follow a cursor. Mirrors
 * `CONNECTIONS_LIST_MAX_LIMIT` in the backend's connection entity; both copies
 * are pinned to behaviour by `backend/tests/contracts.test.ts`.
 */
export const CONNECTIONS_LIST_MAX_LIMIT = 1000;
export const ConnectionsListQuerySchema = z.object({
    eventId: ConnectionEventIdSchema,
    limit: z.coerce.number().int().min(1).max(CONNECTIONS_LIST_MAX_LIMIT).optional(),
    cursor: z.string().min(1).max(512).optional(),
});
//# sourceMappingURL=connections.js.map